import clearAttachmentInfoBarMessages from './clearAttachmentInfoBarMessages';
import filterFilesToAttach from './filterFilesToAttach';
import ifIsOverSizeLimitShowErrorMessage from './ifIsOverSizeLimitShowErrorMessage';
import removeRelatedItemFromUploadQueue from './removeRelatedItemFromUploadQueue';
import addAttachmentIntoWellBeforeUpload from '../../actions/addAttachmentIntoWellBeforeUpload';
import { CreateAttachmentDependencies } from '../../actions/createAttachment';
import createAttachment from '../../actions/createAttachment';
import deleteAttachmentAction from '../../actions/deleteAttachment';
import removeAttachmentFromWell from '../../actions/removeAttachmentFromWell';
import AttachmentWellViewState from '../../schema/AttachmentWellViewState';
import cancelAttachment from '../../services/cancelAttachment';
import { NonExistentMailboxErrorCode } from '../../types/CreateAttachmentError';
import { CreateAttachmentsProperties } from '../../types/CreateAttachmentsProperties';
import convertAttachmentDataProviderTypeToString from '../convertAttachmentDataProviderTypeToString';
import { logUsage } from 'owa-analytics';
import { PerformanceDatapoint } from 'owa-analytics';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { getGuid } from 'owa-guid';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import CreateAttachmentInlineType from 'owa-service/lib/contract/CreateAttachmentInlineType';
import fileAttachment from 'owa-service/lib/factory/fileAttachment';
import item from 'owa-service/lib/factory/item';
import itemAttachment from 'owa-service/lib/factory/itemAttachment';
import itemId from 'owa-service/lib/factory/itemId';
import itemIdAttachment from 'owa-service/lib/factory/itemIdAttachment';
import mimeContentType from 'owa-service/lib/factory/mimeContentType';
import referenceAttachment from 'owa-service/lib/factory/referenceAttachment';
import { trace } from 'owa-trace';
import AttachmentDeleteQueueItem, {
    OnAttachmentDeletedCallback,
} from '../../types/AttachmentDeleteQueueItem';
import AttachmentUploadQueueItem, {
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from '../../types/AttachmentUploadQueueItem';
import showErrorMessageForDeleteFailure, {
    AttachmentDeleteFailureReason,
} from './showErrorMessageForDeleteFailure';
import showErrorMessagesForUploadFailures, {
    AttachmentUploadFailureReason,
    AttachmentUploadItemFailure,
} from './showErrorMessagesForUploadFailures';
import {
    AttachmentFile,
    AttachmentFileList,
    AttachmentFileType,
    AttachmentItemFile,
    Base64InlineImageFile,
    CloudFile,
    LocalComputerFile,
    MailItemFile,
} from 'owa-attachment-file-types';
import {
    ActionMessageId,
    ActionType,
    AttachmentFullViewState,
    AttachmentState,
    AttachmentStateType,
    createAttachmentFullViewState,
    createInlineAttachmentState,
    InlineAttachmentState,
    InlineAttachmentStatus,
    setOngoingActionAndActionMessage,
} from 'owa-attachment-full-data';
import { SpecialCharactersAreNotSupportedError } from '../attachmentConstants';

export interface AttachmentQueueManagerInterface {
    onFinishPromise: Promise<ClientAttachmentId>;

    enqueueAttachmentCreation(
        files: AttachmentFileList,
        properties: CreateAttachmentsProperties,
        onAttachmentCreated?: OnAttachmentCreatedCallback,
        onAttachmentCanceledOrFailed?: OnAttachmentCanceledOrFailedCallback,
        tryAgain?: () => void
    ): AttachmentFileList;

    deleteAttachment(
        attachmentViewState: AttachmentState,
        onAttachmentDeleted?: OnAttachmentDeletedCallback
    );
}

export function getAttachmentQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    parentItemId: ClientItemId,
    infoBar: InfoBarHostViewState,
    onAttachmentCreatedCallback: OnAttachmentCreatedCallback | null = null,
    onAttachmentDeletedCallback: OnAttachmentDeletedCallback | null = null
): AttachmentQueueManagerInterface {
    return new AttachmentQueueManager(
        attachmentWellViewState,
        parentItemId,
        infoBar,
        onAttachmentCreatedCallback,
        onAttachmentDeletedCallback
    );
}

export class AttachmentQueueManager implements AttachmentQueueManagerInterface {
    private uploadQueue: AttachmentUploadQueueItem[];
    private deleteQueue: AttachmentDeleteQueueItem[];
    private uploadFailures: AttachmentUploadItemFailure[];
    private isInProgress: boolean;
    private currentUploadItem: AttachmentUploadQueueItem;
    private isCancelling: boolean;
    private datapoint: PerformanceDatapoint;
    private onFinish: (attachmentId: ClientAttachmentId) => void;
    public onFinishPromise: Promise<ClientAttachmentId>;

    /**
     * The attachment queue manager which is responsible for managing creation and deletion of attachments in an item.
     * @param attachmentWellViewState The attachment well view state for parent item.
     * @param parentItemId The parent item id.
     * @param infoBarAddHandler A function to add message to info bar.
     * @param infoBarRemoveHandler A function to remove message from info bar.
     * @param onAnyAttachmentCreatedInParentItemCallback A callback that will be called whenever an attachment is created in the parent item.
     * @param onAnyAttachmentDeletedInParentItemCallback A callback that will be called whenever an attachment is deleted from the parent item.
     */
    constructor(
        private attachmentWellViewState: AttachmentWellViewState,
        private parentItemId: ClientItemId,
        private infoBar: InfoBarHostViewState,
        private onAnyAttachmentCreatedInParentItemCallback: OnAttachmentCreatedCallback | null = null,
        private onAnyAttachmentDeletedInParentItemCallback: OnAttachmentDeletedCallback | null = null
    ) {
        this.uploadQueue = [];
        this.deleteQueue = [];
        this.uploadFailures = [];
        /* tslint:disable:promise-must-complete */
        this.onFinishPromise = new Promise<ClientAttachmentId>((resolve, reject) => {
            // We will resolve when finishing process.
            this.onFinish = function (attachmentId: ClientAttachmentId) {
                resolve(attachmentId);
            };
        });
        /* tslint:enable:promise-must-complete */
    }

    enqueueAttachmentCreation(
        files: AttachmentFileList,
        properties: CreateAttachmentsProperties,
        onAttachmentCreated: OnAttachmentCreatedCallback = null,
        onAttachmentCanceledOrFailed: OnAttachmentCanceledOrFailedCallback = null,
        tryAgain: () => void = null
    ): AttachmentFileList {
        const { isInline, shouldShare, isHiddenInline, skipProcessInlineImage } = properties;
        // Clear all attachment info bar messages
        clearAttachmentInfoBarMessages(this.infoBar);

        if (
            !shouldShare &&
            ifIsOverSizeLimitShowErrorMessage(files, this.infoBar, this.attachmentWellViewState)
        ) {
            return [];
        }

        files = filterFilesToAttach(files, isInline, this.infoBar);

        for (let i = 0, file: AttachmentFile; (file = files[i]); i++) {
            let attachment: AttachmentType;
            let tempId: string = null;
            const uninitializedAttachment: AttachmentType = {
                Name: null,
                ContentType: null,
                ContentId: null,
                ContentLocation: null,
                AttachmentOriginalUrl: null,
                Size: 0,
                LastModifiedTime: null,
                IsInline: false,
                IsInlineToUniqueBody: false,
                IsInlineToNormalBody: false,
                ThumbnailMimeType: null,
                Thumbnail: null,
            };
            if (shouldShare) {
                const cloudFile = file as CloudFile;
                attachment = referenceAttachment({
                    ...uninitializedAttachment,
                    Name: cloudFile.name,
                    Size: cloudFile.size,
                    ContentType: cloudFile.type,
                    ProviderType: convertAttachmentDataProviderTypeToString(cloudFile.providerType),
                    AttachmentIsFolder: cloudFile.isFolder,
                    IsInline: false,
                    PermissionType: AttachmentPermissionLevel.None,
                    AttachLongPathName: null,
                    ProviderEndpointUrl: null,
                    AttachmentThumbnailUrl: null,
                    AttachmentPreviewUrl: null,
                    OriginalPermissionType: AttachmentPermissionLevel.None,
                });
            } else if (
                file.fileType === AttachmentFileType.Base64InlineImage ||
                file.fileType === AttachmentFileType.ExpirableBase64InlineImage
            ) {
                // This is inline image with base64 string.
                const base64InlineImageFile = file as Base64InlineImageFile;
                attachment = fileAttachment({
                    ...uninitializedAttachment,
                    Name: base64InlineImageFile.name,
                    Size: base64InlineImageFile.size,
                    ContentType: base64InlineImageFile.type,
                    IsInline: true,
                    Content: base64InlineImageFile.dataUri,
                    IsContactPhoto: false,
                    ImageThumbnailSalientRegions: null,
                    ImageThumbnailHeight: 0,
                    ImageThumbnailWidth: 0,
                });
                tempId = base64InlineImageFile.tempId;
            } else if (file.fileType === AttachmentFileType.MailItem) {
                const conversationFile = file as MailItemFile;
                // mimeContent property will be present in the below scenario
                // 1. Compose a non-S/MIME mail
                // 2. Drag a mail attachment
                // 3. Toggle to S/MIME mode (This will add the mimeContent property to the attachmentType object,
                //    itemId property will be removed since any reference to server ids is not required for S/MIME attachments)
                // 4. Toggle back to non-S/MIME mode (Now the attachment has to be uploaded using the mimeContent property instead of the itemId property)
                if (conversationFile.mimeContent) {
                    attachment = itemAttachment({
                        ...uninitializedAttachment,
                        Name: file.name,
                        Size: file.size,
                        IsInline: false,
                        Item: item({
                            MimeContent: mimeContentType({
                                Value: conversationFile.mimeContent,
                            }),
                        }),
                        ContentType: 'message/rfc822',
                    });
                } else {
                    const id = itemId({
                        Id: conversationFile.itemId,
                    });

                    attachment = itemIdAttachment({
                        ...uninitializedAttachment,
                        Name: file.name,
                        IsInline: false,
                        ItemId: id,
                        Size: file.size,
                        AttachmentIdToAttach: null,
                        CreateAttachmentInlineType: CreateAttachmentInlineType.CopyFromSource,
                    });
                }
            } else if (
                file.fileType === AttachmentFileType.AttachmentItem ||
                file.fileType === AttachmentFileType.AttachmentItemSuggestion
            ) {
                const attachmentItem = file as AttachmentItemFile;
                attachment = itemIdAttachment({
                    ...uninitializedAttachment,
                    Name: attachmentItem.name,
                    AttachmentIdToAttach: attachmentItem.attachmentItemId,
                    __type: attachmentItem.type,
                    Size: file.size,
                    ItemId: null,
                    CreateAttachmentInlineType: CreateAttachmentInlineType.CopyFromSource,
                });
            } else if (file.fileType === AttachmentFileType.Uri) {
                attachment = fileAttachment({
                    ...uninitializedAttachment,
                    Name: file.name,
                    AttachmentOriginalUrl: file.uri,
                    Size: file.size,
                    ContentType: file.type,
                    IsInline: isInline,
                    Content: '',
                    IsContactPhoto: false,
                    ImageThumbnailSalientRegions: null,
                    ImageThumbnailHeight: 0,
                    ImageThumbnailWidth: 0,
                });
            } else {
                // This could be a local file, inline image with actual file or a cloud file being attached as a classic attachment.
                attachment = fileAttachment({
                    ...uninitializedAttachment,
                    Name: file.name,
                    Size: file.size,
                    ContentType: file.type,
                    IsInline: isInline,
                    Content: '',
                    IsContactPhoto: false,
                    ImageThumbnailSalientRegions: null,
                    ImageThumbnailHeight: 0,
                    ImageThumbnailWidth: 0,
                });
                tempId = (<LocalComputerFile>file).tempId;
            }

            // We also pass the attachmentTempId as cancellationId
            const attachmentTempId: ClientAttachmentId = {
                Id: tempId ? tempId : getGuid(),
                mailboxInfo: this.parentItemId.mailboxInfo,
            };
            let attachmentState: AttachmentState;
            if (isInline) {
                attachmentState = createInlineAttachmentState(
                    attachmentTempId,
                    attachmentTempId.Id,
                    isHiddenInline,
                    InlineAttachmentStatus.Initialized,
                    skipProcessInlineImage
                );
            } else {
                attachmentState = createAttachmentFullViewState(
                    attachmentTempId,
                    false,
                    false,
                    shouldShare,
                    file.fileType,
                    true
                );
                if (properties.isAddedFromSmartDoc) {
                    attachmentState.fileType = AttachmentFileType.SmartDoc;
                }
                if (this.attachmentWellViewState.supportedMenuActions) {
                    attachmentState.strategy.supportedMenuActions = this.attachmentWellViewState.supportedMenuActions;
                }
                if (properties.downsellFromLink) {
                    setOngoingActionAndActionMessage(
                        attachmentState as AttachmentFullViewState,
                        ActionType.ConvertCloudyToClassic,
                        ActionMessageId.ConvertCloudyToClassicInProgress
                    );
                }
            }

            addAttachmentIntoWellBeforeUpload(
                attachmentState,
                this.attachmentWellViewState,
                attachment,
                attachmentTempId
            );

            const uploadQueueItem: AttachmentUploadQueueItem = {
                attachmentState: attachmentState,
                file: file,
                attachment: attachment,
                shouldShareInfo: properties,
                cancelPendingUpload: () => {},
                onAttachmentCreatedCallback: onAttachmentCreated,
                onAttachmentCanceledOrFailedCallback: onAttachmentCanceledOrFailed,
                tryAgainCallback: tryAgain,
            };

            trace.info('Attachment: UploadQueue enqueue attachment: Name=' + file.name);
            this.uploadQueue.push(uploadQueueItem);
        }

        this.startProcessIfNotStarted();

        return files;
    }

    deleteAttachment(
        attachmentState: AttachmentState,
        onAttachmentDeleted: OnAttachmentDeletedCallback = null
    ) {
        // Clear all attachment info bar messages
        clearAttachmentInfoBarMessages(this.infoBar);

        const isInline = attachmentState.attachmentType === AttachmentStateType.InlineAttachment;

        // Give some user feedback when deleting an attachment in the AttachmentWell
        if (!isInline) {
            setOngoingActionAndActionMessage(
                attachmentState as AttachmentFullViewState,
                ActionType.Delete,
                ActionMessageId.DeleteInProgress
            );
        }

        // We need the attachmentId from server to delete an attachment.
        // If it's inline attachment, it has status other than Initialized when we receive attachmentId from server;
        // else we would set actionCompletePercent to 1.1 when we receive attachmentId from server.
        // If we run into the scenario that server has finished the uploading but hasn't return it to client,
        // cancelAttachment will be able to find the created attachment and remove it.
        const isAttachmentPendingUpload = isInline
            ? (<InlineAttachmentState>attachmentState).status === InlineAttachmentStatus.Initialized
            : (<AttachmentFullViewState>attachmentState).actionCompletePercent <= 1;
        if (isAttachmentPendingUpload && this.currentUploadItem) {
            if (
                this.currentUploadItem.attachmentState.attachmentId === attachmentState.attachmentId
            ) {
                trace.info(
                    'Attachment: Cancelling attachment: TempId=' + attachmentState.attachmentId
                );

                const currentUploadItem = this.currentUploadItem;
                currentUploadItem.cancelPendingUpload();
                if (
                    attachmentState.attachmentType === AttachmentStateType.Attachment &&
                    (<AttachmentFullViewState>attachmentState).isCloudy
                ) {
                    trace.info(
                        `Attachment: Put attachment: Id= ${attachmentState.attachmentId} into deleteQueue`
                    );
                    if (
                        this.deleteQueue.filter(
                            item =>
                                item.attachmentState.attachmentId === attachmentState.attachmentId
                        ).length === 0
                    ) {
                        // If the attachment state does not exist already in the queue then add it to the delete queue
                        const deleteQueueItem: AttachmentDeleteQueueItem = {
                            attachmentState: attachmentState,
                            onAttachmentDeletedCallback: onAttachmentDeleted,
                        };
                        this.deleteQueue.push(deleteQueueItem);
                    }
                    this.startProcessIfNotStarted();
                } else if (!this.isCancelling) {
                    this.isCancelling = true;
                    cancelAttachment(attachmentState.attachmentId.Id).then(succeeded => {
                        this.isCancelling = false;
                        if (succeeded) {
                            removeAttachmentFromWell(this.attachmentWellViewState, attachmentState);
                            if (currentUploadItem.onAttachmentCanceledOrFailedCallback) {
                                currentUploadItem.onAttachmentCanceledOrFailedCallback(
                                    true /* canceled */
                                );
                            }
                        } else {
                            this.onAttachmentDeleteFailure(attachmentState);
                        }
                    });
                }
            } else {
                trace.info(
                    'Attachment: Removing attachment: TempId= ' +
                        attachmentState.attachmentId +
                        ' from queue'
                );
                removeRelatedItemFromUploadQueue(attachmentState, this.uploadQueue);
                removeAttachmentFromWell(this.attachmentWellViewState, attachmentState);
            }
        } else {
            trace.info(
                `Attachment: Put attachment: Id= ${attachmentState.attachmentId} into deleteQueue`
            );
            if (
                this.deleteQueue.filter(
                    item => item.attachmentState.attachmentId === attachmentState.attachmentId
                ).length === 0
            ) {
                // If the attachment state does not exist already in the queue then add it to the delete queue
                const deleteQueueItem: AttachmentDeleteQueueItem = {
                    attachmentState: attachmentState,
                    onAttachmentDeletedCallback: onAttachmentDeleted,
                };
                this.deleteQueue.push(deleteQueueItem);
            }
            this.startProcessIfNotStarted();
        }
    }

    private onAttachmentDeleteFailure(attachmentState: AttachmentState) {
        showErrorMessageForDeleteFailure(
            { item: attachmentState, reason: AttachmentDeleteFailureReason.GenericError },
            this.infoBar
        );

        if (attachmentState.attachmentType !== AttachmentStateType.InlineAttachment) {
            setOngoingActionAndActionMessage(
                attachmentState as AttachmentFullViewState,
                ActionType.None,
                ActionMessageId.None
            );
        }

        trace.warn(
            'Attachment: Failed to cancel attachment: TempId=' + attachmentState.attachmentId
        );
    }

    private startProcessIfNotStarted() {
        if (!this.isInProgress) {
            this.datapoint = new PerformanceDatapoint('AttachmentQueueManager');
            this.isInProgress = true;
            this.processNextAttachment('NewProcess');
        }
    }

    private processNextAttachment(sourceForTrace: string) {
        trace.info('Attachment: Invoking processNextAttachment from: ' + sourceForTrace);
        if (this.deleteQueue.length !== 0) {
            const { attachmentState, onAttachmentDeletedCallback } = this.deleteQueue[0];
            const attachmentModel = getAttachment(attachmentState.attachmentId);
            trace.info('Attachment: Deleting attachment: Id= ' + attachmentState.attachmentId);

            deleteAttachmentAction(
                this.attachmentWellViewState,
                attachmentState,
                this.parentItemId,
                attachmentState.fileType
            )
                .then(() => {
                    // We call shift here instead of when setting attachmentState to avoid situations where we send
                    // multiple delete requests for the same attachment. By keeping the attachment in the queue until delete
                    // finishes the check "if (!this.deleteQueue.includes(attachmentState))" will fail and we will only send one request (as desired)
                    this.deleteQueue.shift();

                    // After successfull deletion, invoke the callback
                    safelyInvokeCallback(
                        onAttachmentDeletedCallback,
                        this.parentItemId,
                        attachmentState.attachmentId,
                        attachmentModel.model
                    );
                    safelyInvokeCallback(
                        this.onAnyAttachmentDeletedInParentItemCallback,
                        this.parentItemId,
                        attachmentState.attachmentId,
                        attachmentModel.model
                    );

                    this.processNextAttachment('DeleteAttachmentFinish');
                })
                .catch(error => {
                    this.deleteQueue.shift();
                    this.onAttachmentDeleteFailure(attachmentState);
                    this.logDeletionFailure(error.message, attachmentState);
                    this.processNextAttachment('DeleteAttachmentFinish');
                });
        } else if (this.uploadQueue.length !== 0) {
            this.currentUploadItem = this.uploadQueue.shift();
            const onAttachmentCreated = this.currentUploadItem.onAttachmentCreatedCallback;
            const dependencies: CreateAttachmentDependencies = {
                attachmentWellViewState: this.attachmentWellViewState,
                parentItemId: this.parentItemId,
                infoBar: this.infoBar,
            };
            const { createAttachmentPromise, cancelCreateAttachment } = createAttachment(
                this.parentItemId,
                this.currentUploadItem.attachment,
                this.currentUploadItem.attachmentState.attachmentId.Id,
                this.currentUploadItem.file,
                this.currentUploadItem.attachmentState,
                dependencies,
                this.currentUploadItem.shouldShareInfo
            );

            this.currentUploadItem.cancelPendingUpload = cancelCreateAttachment;

            createAttachmentPromise
                .then((attachment: AttachmentType) => {
                    if (attachment) {
                        // After successfull creation, invoke the callback
                        const attachmentId = {
                            ...attachment.AttachmentId,
                            mailboxInfo: this.parentItemId.mailboxInfo,
                        };
                        safelyInvokeCallback(
                            onAttachmentCreated,
                            this.parentItemId,
                            attachmentId,
                            attachment
                        );
                        safelyInvokeCallback(
                            this.onAnyAttachmentCreatedInParentItemCallback,
                            this.parentItemId,
                            attachmentId,
                            attachment
                        );
                    }
                    this.processNextAttachment('CreateAttachmentFinish');
                })
                .catch(error => {
                    let errorReason = AttachmentUploadFailureReason.GenericError;

                    // For now not support special character in filename when attach classic attachment
                    if (error?.message === SpecialCharactersAreNotSupportedError) {
                        errorReason =
                            AttachmentUploadFailureReason.SpecialCharactersAreNotSupported;
                    }
                    // For now nonExistant mailbox is the only non-generic error we handle
                    if (error && error.errorCode === NonExistentMailboxErrorCode) {
                        errorReason = AttachmentUploadFailureReason.NonExistentMailbox;
                    }
                    // Add the failure to the upload failures
                    this.uploadFailures.push({
                        item: this.currentUploadItem,
                        reason: errorReason,
                    });
                    if (this.currentUploadItem.onAttachmentCanceledOrFailedCallback) {
                        this.currentUploadItem.onAttachmentCanceledOrFailedCallback(
                            false /* canceled */
                        );
                    }
                    this.logCreationFailure();
                    this.processNextAttachment('CreateAttachmentFinish');
                });
        } else {
            this.finishProcess();
        }
    }

    private finishProcess() {
        trace.info('Attachment: AttachmentQueueManager: finishProcess');
        this.isInProgress = false;
        const attachmentId =
            this.currentUploadItem && this.currentUploadItem.attachmentState
                ? this.currentUploadItem.attachmentState.attachmentId
                : null;
        this.currentUploadItem = null;
        this.onFinish(attachmentId);
        this.datapoint.end();
        this.datapoint = null;

        // Show error messages for upload failures
        showErrorMessagesForUploadFailures(this.uploadFailures, this.infoBar);
        this.uploadFailures = [];
    }

    private logCreationFailure() {
        logUsage('AttachmentCreationFailure');
    }

    private logDeletionFailure(error: string, attachmentState: AttachmentState) {
        logUsage('AttachmentDeletionFailure', [error], {
            cosmosOnlyData: JSON.stringify({
                attachmentId: attachmentState.attachmentId.Id,
            }),
        });
    }
}

function safelyInvokeCallback<T extends OnAttachmentCreatedCallback | OnAttachmentDeletedCallback>(
    callback: T | null,
    parentItemId: ClientItemId,
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType
) {
    if (callback) {
        callback(parentItemId, attachmentId, attachment);
    }
}
